import { firstDefined } from '@avantia/client-and-server-utilities';
import { HpStringLengthValidator } from '@avantia/form-validation';
import { createControlId } from '@avantia/questionset-model';
import React, { useEffect } from 'react';
import { HpClientStandardInputTextAndStyle, HpControlValueTypes, HpEventTypes } from '../../../types';
import { HpInputControlProps, HpOnFormEvent, OptionalQuestionUI } from '../../../types/inputControlTypes';
import {
  HpCreateFormGroupCustomWidget,
  HpErrorPositionTypes,
  StandardFormGroupWidget
} from '../../widgets/standardFormGroupWidget';
import { BasicTextInputControl } from '../common/basicTextInputControl';

export interface HpTextInputControlProps extends HpInputControlProps {
  placeholder?: string;
  prefixText?: string;
  suffixText?: string;
  formatter?: HpTextInputControlFormatter;
  errorPosition?: HpErrorPositionTypes;
  createFormGroupCustomWidget?: HpCreateFormGroupCustomWidget;
}

export interface HpTextInputControlFormatter {
  do?: (input: HpControlValueTypes, ui: OptionalQuestionUI) => string;
  undo?: (input: HpControlValueTypes, ui: OptionalQuestionUI, eventType: HpEventTypes) => string;
}

const defaultFormatter: HpTextInputControlFormatter = {
  undo: (val: HpControlValueTypes, ui: OptionalQuestionUI, eventType: HpEventTypes): string =>
    eventType === 'blur' ? (val as string).trim() : (val as string)
};

export function TextInputControl({
  answerSource,
  createFormGroupCustomWidget,
  data,
  dataType,
  disabled,
  errorPosition,
  errors,
  formatter,
  formatType,
  id,
  name,
  onBlur,
  onChange,
  onFocus,
  prefixText,
  question,
  questionText,
  subtext,
  suffixText,
  type,
  ui,
  value
}: HpTextInputControlProps): JSX.Element {
  const error = errors ? errors[id] : undefined;
  const { textAndStyle: dataTextAndStyle, validation } = data;
  const textAndStyle = dataTextAndStyle[formatType] as HpClientStandardInputTextAndStyle;
  const { do: format, undo: unformat } = formatter || defaultFormatter;
  const { expandToFit, isMultiLine, keyboard, lineCount, placeholder, width, privacyLevel, hasCharacterCount } =
    textAndStyle;
  const stringLengthValidator = validation.filter((v) => v.getType() === 'string-length')[0] as
    | HpStringLengthValidator
    | undefined;
  let maxLength: number | undefined;
  let minLength: number | undefined;
  const style: React.CSSProperties | undefined = isMultiLine ? { resize: 'none' } : undefined;
  if (stringLengthValidator) {
    maxLength = stringLengthValidator.getProps().maxLength;
    minLength = stringLengthValidator.getProps().minLength;
  }

  if (isMultiLine && expandToFit) {
    useEffect(() => {
      resizeMultilineInput(id);
    }, []);
  }

  const onChangeHandler = createTextInputOnChangeHandler({
    onChange,
    expandToFit: !!(isMultiLine && expandToFit),
    questionId: id
  });

  const control = (
    <BasicTextInputControl
      disabled={disabled}
      eventDetail={{ type: 'standard', question, unformat }}
      hasCharacterCount={hasCharacterCount}
      id={id}
      isMultiLine={isMultiLine}
      keyboard={keyboard}
      lineCount={lineCount}
      maxLength={maxLength}
      minLength={minLength}
      name={name}
      onBlur={onBlur}
      onChange={onChangeHandler}
      onFocus={onFocus}
      placeholder={firstDefined(placeholder, questionText)}
      prefixText={prefixText}
      privacyLevel={privacyLevel}
      questionText={questionText}
      style={style}
      suffixText={suffixText}
      type={type}
      value={format ? format(value, ui) : value}
      width={width}
    />
  );

  return (
    <StandardFormGroupWidget
      answerSource={answerSource}
      control={control}
      controlType="text-input"
      createFormGroupCustomWidget={createFormGroupCustomWidget}
      dataType={dataType}
      error={error}
      errorPosition={errorPosition}
      helpInfo={data.helpInfo}
      helpInfoUI={ui.helpInfo}
      id={id}
      onClick={onChange}
      question={question}
      questionText={questionText}
      subtext={subtext}
      textAndStyle={textAndStyle}
    />
  );
}

function resizeMultilineInput(questionId: string) {
  const controlId = createControlId({ questionId });
  const $controlElement = $(`#${controlId}`);
  if ($controlElement) {
    $controlElement.css('overflow', 'hidden');
    const controlElement = $controlElement[0];
    const { scrollHeight, offsetHeight } = controlElement || {};
    if (scrollHeight) {
      if (!offsetHeight || scrollHeight >= offsetHeight) {
        $controlElement.css('height', `${scrollHeight}px`);
      } else {
        $controlElement.css('height', `auto`);
      }
    }
  }
}

interface CreateTextInputOnChangeHandlerProps {
  expandToFit: boolean;
  onChange: HpOnFormEvent;
  questionId: string;
}

function createTextInputOnChangeHandler({
  expandToFit,
  onChange,
  questionId
}: CreateTextInputOnChangeHandlerProps): HpOnFormEvent {
  return (e, detail) => {
    onChange(e, detail);
    if (expandToFit) {
      resizeMultilineInput(questionId);
    }
  };
}
